import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './DashboardBackgroundModal.css';

const DashboardBackgroundModal = ({ 
  isOpen, 
  onClose, 
  dashboardId, 
  currentBackground,
  onPendingBackgroundChange
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(currentBackground?.background_image_url || null);
  const [opacity, setOpacity] = useState(currentBackground?.background_opacity || 1.0);
  const [position, setPosition] = useState(currentBackground?.background_position || 'center');
  const [size, setSize] = useState(currentBackground?.background_size || 'cover');
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (currentBackground) {
      setPreviewUrl(currentBackground.background_image_url);
      setOpacity(currentBackground.background_opacity);
      setPosition(currentBackground.background_position);
      setSize(currentBackground.background_size);
    }
  }, [currentBackground]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    const file = fileInputRef.current?.files[0];
    const hasExistingBackground = !!currentBackground?.background_image_url;
    const settingsChanged = (
      opacity !== (currentBackground?.background_opacity ?? 1.0) ||
      position !== (currentBackground?.background_position ?? 'center') ||
      size !== (currentBackground?.background_size ?? 'cover')
    );

    // 변경 사항 없으면 안내
    if (!file && !settingsChanged) {
      window.alert('변경 사항이 없습니다.');
      return;
    }

    // 파일도 없고 기존 배경도 없으면 안내
    if (!file && !hasExistingBackground) {
      window.alert('먼저 배경 이미지를 업로드해주세요.');
      return;
    }

    // 서버에 즉시 업로드하지 않고, 부모로 미반영 상태를 전달
    const pending = {
      file: file || null,
      previewUrl: previewUrl || null,
      background_opacity: opacity,
      background_position: position,
      background_size: size,
      imageChanged: !!file,
      settingsChanged
    };

    if (typeof onPendingBackgroundChange === 'function') {
      onPendingBackgroundChange(pending);
    }
    window.alert('배경 설정이 편집 상태에 반영되었습니다. 저장 시 서버에 적용됩니다.');
    onClose();
  };

  const handleRemoveBackground = async () => {
    if (!window.confirm('배경 이미지를 제거하시겠습니까?')) {
      return;
    }

    // 서버 호출 없이 제거 플래그만 부모에 전달
    if (typeof onPendingBackgroundChange === 'function') {
      onPendingBackgroundChange({
        removeFlag: true
      });
    }
    setPreviewUrl(null);
    window.alert('배경 이미지 제거가 편집 상태에 반영되었습니다. 저장 시 서버에 적용됩니다.');
    onClose();
  };

  // 모달을 body에 직접 렌더링하기 위해 Portal 사용
  const modalContent = (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content background-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>대시보드 배경 이미지 설정</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {/* 이미지 업로드 섹션 */}
          <div className="upload-section">
            <h3>배경 이미지</h3>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="file-input"
            />
            <div className="upload-buttons">
              <button 
                className="file-select-btn"
                onClick={() => fileInputRef.current?.click()}
              >
                {currentBackground?.background_image_url ? '이미지 변경' : '이미지 선택'}
              </button>
              {currentBackground?.background_image_url && (
                <span className="current-image-info">
                  현재 이미지가 설정되어 있습니다
                </span>
              )}
            </div>
          </div>

          {/* 미리보기 섹션 */}
          {previewUrl && (
            <div className="preview-section">
              <h3>미리보기</h3>
              <div 
                className="preview-container"
                style={{
                  backgroundImage: `url(${previewUrl})`,
                  backgroundSize: size,
                  backgroundPosition: position,
                  opacity: opacity,
                }}
              >
                <div className="preview-content">
                  대시보드 컨텐츠 예시
                </div>
              </div>
            </div>
          )}

          {/* 설정 섹션 */}
          <div className="settings-section">
            <h3>배경 설정</h3>
            
            <div className="setting-item">
              <label>투명도:</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={opacity}
                onChange={(e) => setOpacity(parseFloat(e.target.value))}
              />
              <span>{opacity}</span>
            </div>

            <div className="setting-item">
              <label>위치:</label>
              <select value={position} onChange={(e) => setPosition(e.target.value)}>
                <option value="center">가운데</option>
                <option value="top">상단</option>
                <option value="bottom">하단</option>
                <option value="left">왼쪽</option>
                <option value="right">오른쪽</option>
                <option value="top left">상단 왼쪽</option>
                <option value="top right">상단 오른쪽</option>
                <option value="bottom left">하단 왼쪽</option>
                <option value="bottom right">하단 오른쪽</option>
              </select>
            </div>

            <div className="setting-item">
              <label>크기:</label>
              <select value={size} onChange={(e) => setSize(e.target.value)}>
                <option value="cover">화면 채우기 (cover)</option>
                <option value="contain">전체 보기 (contain)</option>
                <option value="auto">원본 크기 (auto)</option>
                <option value="100% 100%">늘이기 (stretch)</option>
              </select>
            </div>
          </div>
        </div>

        {/* 버튼 바: 흰색 배경 컨테이너 */}
        <div className="modal-button-bar">
          {currentBackground?.background_image_url && (
            <button
              className="remove-btn"
              onClick={handleRemoveBackground}
              disabled={isUploading}
            >
              배경 제거
            </button>
          )}
          <button
            className="upload-btn"
            onClick={handleUpload}
            disabled={isUploading}
          >
            {isUploading ? '처리 중...' : '확인'}
          </button>
          <button className="cancel-btn" onClick={onClose}>
            취소
          </button>
        </div>
      </div>
    </div>
  );
  
  // Portal을 사용하여 body에 직접 렌더링
  return isOpen ? ReactDOM.createPortal(modalContent, document.body) : null;
};

export default DashboardBackgroundModal;